import { isNotNullUnit } from '../../unit';
import { ExprType, makeNullable } from '../expr_types';

/**
 * Makes a best-effort attempt to return a type that both `a` and `b` can be
 * converted to. When there is no such type, or when the implementation gets too
 * annoying, returns its first argument.
 *
 * Callers must call `convertToType` with the result, because that function
 * makes the final determination of whether the recommended conversion actually
 * works. So the implementation pairs with that of `convertToType`.
 */
export function bestCompatibleType(a: ExprType, b: ExprType): ExprType {
  const tryNullable = (expr: ExprType): ExprType => {
    try {
      return makeNullable(expr);
    } catch {
      return a;
    }
  };

  // If one of the types is nullable, the best compatible type is also nullable.
  if (a.ty === 'nullable') {
    return tryNullable(bestCompatibleType(a.baseTy, b));
  }
  if (b.ty === 'nullable') {
    return tryNullable(bestCompatibleType(a, b.baseTy));
  }
  if (a.ty === 'null') {
    return tryNullable(b);
  }
  if (b.ty === 'null') {
    return tryNullable(a);
  }

  // If both are quantities, we should union the types
  if (a.ty === 'quantity' && b.ty === 'quantity') {
    const uniqueUnits = new Set([...a.units, ...b.units]);
    return { ty: 'quantity', units: Array.from(uniqueUnits) };
  }

  // Quantities are more expressive than numbers, so prefer them.
  if (a.ty === 'quantity' && b.ty === 'number') {
    return a;
  }
  if (b.ty === 'quantity' && a.ty === 'number') {
    return b;
  }

  // Values with units are preferred to values without units.
  if (a.ty === 'number' && b.ty === 'number') {
    if (isNotNullUnit(a.unit)) {
      return a;
    }
    if (isNotNullUnit(b.unit)) {
      return b;
    }
  }

  // Datetimes are more expressive than dates, so prefer them.
  if (a.ty === 'datetime' && b.ty === 'date') {
    return a;
  }
  if (b.ty === 'datetime' && a.ty === 'date') {
    return b;
  }

  return a;
}
